/**
 * Admin Chat Monitoring Service Layer
 * Handles all API calls for admin chat monitoring functionality
 */

import { getAuthHeaders } from './authService';
import { API_ENDPOINTS } from './config/apiConfig';

const ADMIN_CHAT_BASE = `${API_ENDPOINTS.ADMIN_BASE}/chat`;

/**
 * Helper function to handle API responses
 * @param {Response} response - Fetch response object
 * @returns {Promise<object>} Parsed JSON data
 */
async function handleResponse(response) {
  const data = await response.json();

  if (!response.ok) {
    console.error('API Error Response:', data);
    const errorMessage = data.error || data.message || (data.errors ? JSON.stringify(data.errors) : 'An error occurred');
    throw new Error(errorMessage);
  }

  return data;
}

// ============= CHAT STATISTICS =============

/**
 * Get chat monitoring dashboard statistics
 * @returns {Promise<object>} Chat statistics
 */
export async function getChatStats() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_CHAT_BASE}/stats`, { headers });
  return handleResponse(response);
}

// ============= CONVERSATIONS =============

/**
 * Get all conversations with optional search/filtering
 * @param {object} filters - { search, limit, offset }
 * @returns {Promise<object>} Conversations list
 */
export async function getAllConversations(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_CHAT_BASE}/conversations?${params}`, { headers });
  return handleResponse(response);
}

/**
 * Get messages for a specific conversation
 * @param {string} conversationId - Conversation UUID
 * @param {object} filters - { limit, offset }
 * @returns {Promise<object>} Messages list
 */
export async function getConversationMessages(conversationId, filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_CHAT_BASE}/conversations/${conversationId}/messages?${params}`, { headers });
  return handleResponse(response);
}

// ============= MESSAGE ACTIONS =============

/**
 * Delete an inappropriate message
 * @param {string} messageId - Message UUID
 * @param {string} reason - Reason for deletion
 * @returns {Promise<object>} Success response
 */
export async function deleteMessage(messageId, reason) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_CHAT_BASE}/messages/${messageId}`, {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ reason })
  });
  return handleResponse(response);
}

/**
 * Flag a message as inappropriate
 * @param {string} messageId - Message UUID
 * @param {string} reason - Reason for flagging
 * @param {string} category - Flag category (inappropriate, spam, harassment, other)
 * @returns {Promise<object>} Flag object
 */
export async function flagMessage(messageId, reason, category = 'inappropriate') {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_CHAT_BASE}/messages/${messageId}/flag`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ reason, category })
  });
  return handleResponse(response);
}

// ============= FLAGGED MESSAGES =============

/**
 * Get all flagged messages with optional filters
 * @param {object} filters - { status, limit, offset }
 * @returns {Promise<object>} Flagged messages list
 */
export async function getFlaggedMessages(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_CHAT_BASE}/flags?${params}`, { headers });
  return handleResponse(response);
}

/**
 * Update flag status and review notes
 * @param {string} flagId - Flag UUID
 * @param {string} status - New status (pending, reviewed, dismissed, actioned)
 * @param {string} reviewNotes - Optional review notes
 * @returns {Promise<object>} Updated flag object
 */
export async function updateFlag(flagId, status, reviewNotes = '') {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_CHAT_BASE}/flags/${flagId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ status, review_notes: reviewNotes })
  });
  return handleResponse(response);
}

// ============= USER WARNINGS =============

/**
 * Send a warning to a user
 * @param {object} warningData - {
 *   user_id: string,
 *   user_email: string,
 *   user_type: 'student' | 'teacher',
 *   warning_type: string,
 *   warning_message: string,
 *   severity: 'low' | 'medium' | 'high' | 'critical',
 *   related_conversation_id?: string,
 *   related_message_id?: string
 * }
 * @returns {Promise<object>} Warning object
 */
export async function sendWarningToUser(warningData) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_CHAT_BASE}/warnings`, {
    method: 'POST',
    headers,
    body: JSON.stringify(warningData)
  });
  return handleResponse(response);
}

/**
 * Get all warnings with optional filters
 * @param {object} filters - { user_id, acknowledged, limit, offset }
 * @returns {Promise<object>} Warnings list
 */
export async function getAllWarnings(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_CHAT_BASE}/warnings?${params}`, { headers });
  return handleResponse(response);
}

// ============= EXPORT =============

/**
 * Export a conversation as PDF or CSV
 * @param {string} conversationId - Conversation UUID
 * @param {string} format - 'pdf' or 'csv'
 * @returns {Promise<Blob|string>} File blob (PDF) or CSV string
 */
export async function exportConversation(conversationId, format = 'pdf') {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_CHAT_BASE}/export`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ conversation_id: conversationId, format })
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Export failed');
  }

  if (format === 'pdf') {
    // Return blob for PDF
    return await response.blob();
  } else {
    // Return text for CSV
    return await response.text();
  }
}

/**
 * Download exported conversation file
 * @param {string} conversationId - Conversation UUID
 * @param {string} format - 'pdf' or 'csv'
 * @param {string} filename - Optional custom filename
 */
export async function downloadConversationExport(conversationId, format = 'pdf', filename = null) {
  try {
    const data = await exportConversation(conversationId, format);

    // Create download link
    const url = window.URL.createObjectURL(format === 'pdf' ? data : new Blob([data], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `conversation-${conversationId}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Error downloading export:', error);
    throw error;
  }
}

// ============= AUTO-SCANNING =============

/**
 * Automatically scan recent messages for suspicious content
 * @param {number} hoursBack - How many hours back to scan (default: 24, max: 168)
 * @returns {Promise<object>} Scan results { scanned, flagged, message }
 */
export async function autoScanMessages(hoursBack = 24) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_CHAT_BASE}/auto-scan`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ hours_back: hoursBack })
  });
  return handleResponse(response);
}
